import { resolveWebviewDeeplinkUrl } from '../resolveWebviewDeeplinkUrl';

/**
 * `ably://webview-bottomsheet` 가 아니라 **일반 `ably://webview`** 로 와도 전체 화면 웹뷰
 * 대신 바텀시트로 띄울 경로.
 *
 * 서버가 같은 목적지를 두 형태로 내려주는 경우가 있어(전체 화면 웹뷰 딥링크로 내려오지만
 * 시트로 보여야 하는 카테고리 전체보기 등), 경로 기준으로 여기서 흡수한다.
 */
export const WEBVIEW_BOTTOM_SHEET_PATHS: readonly string[] = ['/place/overview'];

export type WebviewBottomSheetTarget = {
  /** 시트에 띄울 절대 URL (path 로 온 경우 환경 origin 이 붙는다) */
  url: string;
  /** 딥링크의 `height_ratio`(0~100). 없으면 null → 전체 높이 */
  heightRatio: number | null;
};

/** `ably://webview-bottomsheet...` 도 이 prefix 로 시작한다(호스트 판별은 아래에서 따로 한다) */
const WEBVIEW_DEEPLINK_PREFIX = 'ably://webview';
const BOTTOM_SHEET_DEEPLINK_PREFIX = 'ably://webview-bottomsheet';

const getHeightRatio = (deeplink: string): number | null => {
  const match = deeplink.match(/[?&]height_ratio=(\d+)/);
  return match === null ? null : Number(match[1]);
};

const getPathname = (url: string): string | null => {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
};

const isBottomSheetPath = (url: string): boolean => {
  const pathname = getPathname(url);
  if (pathname === null) return false;

  const normalized =
    pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
  return WEBVIEW_BOTTOM_SHEET_PATHS.includes(normalized);
};

/**
 * 딥링크가 **바텀시트로 띄울 대상**인지 판별하고, 시트에 넘길 값을 뽑는다(아니면 null).
 *
 * 대상은 두 가지다.
 * - `ably://webview-bottomsheet?url=...` — 호스트 자체가 바텀시트
 * - `ably://webview?url=...` 중 경로가 `WEBVIEW_BOTTOM_SHEET_PATHS` 에 있는 것
 *
 * `height_ratio` 는 딥링크 자신의 파라미터라 `url` 값에 포함되지 않는다(`extractWebviewUrlParam`
 * 이 첫 `&` 에서 잘라낸다). 그래서 url 과 따로 뽑아 넘긴다.
 */
export const resolveWebviewBottomSheetTarget = (
  deeplink: string,
): WebviewBottomSheetTarget | null => {
  if (!deeplink.startsWith(WEBVIEW_DEEPLINK_PREFIX)) return null;

  // 웹 미지원(네이티브 전용) 경로면 null → 호출부가 기존 흐름(앱 다운로드 유도)으로 간다.
  const url = resolveWebviewDeeplinkUrl(deeplink);
  if (url === null) return null;

  const isBottomSheetHost = deeplink.startsWith(BOTTOM_SHEET_DEEPLINK_PREFIX);
  if (!isBottomSheetHost && !isBottomSheetPath(url)) return null;

  return { url, heightRatio: getHeightRatio(deeplink) };
};